import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { Me } from '@shared/types/factory'
import type { User } from '@/types/User'
import { authClient } from '@/lib/auth-client'
import { postAuthEvent } from '@/lib/auth-channel'
import { clearPrefsOnSignOut, prefStateStorage, readPref, writePref } from '@/lib/ui-prefs'
import { AUTH_IN_PROGRESS, clearAuthMarkers } from '@/lib/auth-prefs'
import { clearLoaderCache } from '@/hooks/loader-store'
import { rememberedUser } from '@/lib/launch-user'
// session-exit imports this store too; each only uses the other inside functions, so the cycle is safe.
import { beginSessionExit, cancelSessionExit } from '@/utils/session-exit'

const isDev = () => process.env.NODE_ENV === 'development'

/** The session answer (/api/auth/session, /api/auth/refresh, accepting an invite). */
export interface AuthResponse {
  token?: string;
  pending_invite_token?: string | null;
  user?: Me;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  authAttempted: boolean;
  /** /auth/session failed with no answer or a server error at page load: still signed in (not persisted). */
  sessionUnreachable: boolean;
  /**
   * The page-load session check has answered (not persisted). Until then `user` may be the person
   * remembered from the last launch, with no token (lib/launch-cache.ts): the app draws for them,
   * and API calls wait (lib/session-ready.ts).
   */
  sessionChecked: boolean;
  /** Renewing the session mid-session failed the same way: a banner offers Retry (not persisted). */
  sessionRefreshFailed: boolean;
  /**
   * The tab is signing out: the pages are swapped for this label so they unmount before the
   * session is cleared. See utils/session-exit.ts (not persisted).
   */
  sessionExit: { label: string } | null;
  login: () => Promise<void>;
  /**
   * Signs out, clears this browser's session data, then loads `loginPath` (default `/login`) as a
   * full page, so no store keeps the last session's data in memory. If the server didn't end the
   * session, the tab is only signed out here and goes to /login without a reload. Ask about unsaved
   * work first.
   */
  logout: (loginPath?: string) => Promise<void>;
  setUser: (user: User | null) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setAuthAttempted: (attempted: boolean) => void;
  setSessionUnreachable: (unreachable: boolean) => void;
  setSessionChecked: (checked: boolean) => void;
  setSessionRefreshFailed: (failed: boolean) => void;
  setSessionExit: (sessionExit: { label: string } | null) => void;
  /** Install a session answer: the token and the user. */
  updateUserAndToken: (userData: AuthResponse) => void;
  /** A change to the signed-in user (a new handle, name or photo) without a new token. */
  updateMe: (change: Partial<Me>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // A tab's own stored user (below) wins over the remembered one.
      user: rememberedUser(),
      loading: true,
      error: null,
      authAttempted: false,
      sessionUnreachable: false,
      sessionChecked: false,
      sessionRefreshFailed: false,
      sessionExit: null,
      
      setUser: (user) => set({ user }),
      setError: (error) => set({ error }),
      setLoading: (loading) => set({ loading }),
      setAuthAttempted: (authAttempted) => set({ authAttempted }),
      setSessionUnreachable: (sessionUnreachable) => set({ sessionUnreachable }),
      setSessionChecked: (sessionChecked) => set({ sessionChecked }),
      setSessionRefreshFailed: (sessionRefreshFailed) => set({ sessionRefreshFailed }),
      setSessionExit: (sessionExit) => set({ sessionExit }),
      updateUserAndToken: (userData) => {
        if (!userData.user) {
          // Not the object itself: it carries the token.
          console.error('Invalid user data received');
          set({ loading: false, error: 'Invalid user data received' });
          return;
        }
        const me = userData.user;
        set({
          user: {
            id: me.id,
            email: me.email,
            displayName: me.displayname || '',
            photoURL: me.photourl || '',
            handle: me.handle ?? null,
            is_admin: Boolean(me.is_admin),
            token: userData.token || get().user?.token || '',
            pendingInviteToken: userData.pending_invite_token ?? null,
          },
          loading: false,
          error: null,
          sessionUnreachable: false,
          sessionRefreshFailed: false,
        });
      },

      updateMe: (change) => {
        const { user } = get();
        if (!user) return;
        set({
          user: {
            ...user,
            ...(change.email !== undefined ? { email: change.email } : {}),
            ...(change.displayname !== undefined ? { displayName: change.displayname ?? '' } : {}),
            ...(change.photourl !== undefined ? { photoURL: change.photourl ?? '' } : {}),
            ...(change.handle !== undefined ? { handle: change.handle } : {}),
            ...(change.is_admin !== undefined ? { is_admin: change.is_admin } : {}),
          },
        });
      },

      login: async () => {
        set({ loading: true, error: null, authAttempted: true });
        
        try {
          writePref(AUTH_IN_PROGRESS, true);
          
          const started = await authClient.signIn.social({
            provider: 'google',
            callbackURL: `${window.location.origin}/login`,
            // A refused sign-in (e.g. ?error=not_invited) comes back to the login page, not BetterAuth's.
            errorCallbackURL: `${window.location.origin}/login`,
          });
          if (started?.error) {
            // Refused before leaving for Google (no Google client on this server, the dev OAuth relay down).
            set({ error: started.error.message || 'Could not start Google sign-in', loading: false });
            clearAuthMarkers();
            return;
          }
          
          if (readPref(AUTH_IN_PROGRESS)) {
            setTimeout(() => {
              if (readPref(AUTH_IN_PROGRESS)) {
                if (isDev()) console.log('Auth appears stuck, forcing page reload');
                clearAuthMarkers();
                window.location.reload();
              }
            }, 3000);
          }
          
          // User is set after OAuth return via AuthProvider session exchange
        } catch (err) {
          const errorMessage = (err as Error).message || 'Failed to login';
          set({ error: errorMessage, loading: false });
          console.error(err);
          clearAuthMarkers();
        }
      },
      
      logout: async (loginPath = '/login') => {
        const userId = get().user?.id;
        // Unmount the pages first, so their last clean-ups run before the clear below and their
        // guards don't ask again on the way out (the caller has asked).
        await beginSessionExit('Signing out…');
        let signedOut = false;
        // In the phone app, this phone stops getting their notifications. Loaded here, not imported at
        // the top: the API modules read this store.
        await import('@/lib/native-push').then((m) => m.forgetThisPhone()).catch(() => {});
        // Inbox pushes (T-37) carry task titles: this browser stops getting them before the session
        // ends, so the next person to sign in here doesn't. Imported here, as it uses the API client.
        await import('@/lib/inbox-push').then((m) => m.disablePushHere()).catch(() => undefined);
        try {
          // Bounded, so a server that never answers can't leave the tab on "Signing out…".
          const res = await authClient.signOut({ fetchOptions: { signal: AbortSignal.timeout(10_000) } });
          signedOut = !res?.error;
          if (res?.error) console.error('Sign out request failed:', res.error);
        } catch (err) {
          console.error('Sign out request failed:', err);
        } finally {
          // Always clear local session — remote sign-out can fail when the session is already dead
          set({
            user: null,
            loading: false,
            error: null,
            sessionUnreachable: false,
            sessionRefreshFailed: false,
          });
          clearLoaderCache();
          // Drafts, filters and other per-project data go; layout prefs stay.
          clearPrefsOnSignOut();
          // The browser has one sign-in, so every other tab signs out too.
          if (userId) postAuthEvent({ type: 'signed-out', userId });
          // A full page load, not a client-side route change: zustand stores (products, labels,
          // inbox…) would otherwise keep the last session's data until the next reload. Only once
          // the server has ended the session: a fresh page would otherwise sign straight back in
          // with the same cookie.
          if (signedOut) window.location.assign(loginPath);
          // Otherwise the tab is only signed out here, without a reload: undo the exit, so a sign-in
          // in this tab has its guards and pages back. Callers outside the route guard go to
          // /login themselves.
          else cancelSessionExit();
        }
      }
    }),
    {
      name: 'auth-storage',
      // Per tab. A new tab starts empty and exchanges the sign-in cookie for a session.
      storage: createJSONStorage(() => prefStateStorage('session')),
      partialize: (state) => ({ user: state.user }),
    }
  )
); 